/**
 * SSO API Response Types
 */

// Base response structure
export interface SSOResponse {
  message: string;
  data?: any;
}

// Success response with tokens
export interface SSOSuccessResponse extends SSOResponse {
  message: "tokens_issued";
  data: {
    access_token: string;
    id_token: string;
    refresh_token: string;
    expires_in: number;
  };
}

// Error response types
export interface SSOErrorResponse extends SSOResponse {
  message: string;
  data?: any;
}

// Specific error response for token exchange failures
export interface SSOTokenExchangeFailedResponse extends SSOErrorResponse {
  message: "token_exchange_failed";
  data: {
    request_id: string;
  };
}

/**
 * SSO Request Types
 */

// Auth code exchange request
export interface SSOAuthCodeExchangeRequest {
  auth_code: string;
}

/**
 * SSO Token Storage
 */

// Stored tokens with metadata
export interface SSOTokens {
  access_token: string;
  id_token: string;
  refresh_token: string;
  expires_in: number;
  issued_at: number; // Timestamp when tokens were issued
}

/**
 * SSO Error Handling
 */

// Standardized error object
export interface SSOError {
  message: string;
  requestId?: string;
  technical?: string;
}

// SSO service state
export interface SSOState {
  isLoading: boolean;
  error: SSOError | null;
  isAuthenticated: boolean;
}

/**
 * Type Guards
 */

export function isSSOSuccessResponse(
  response: any
): response is SSOSuccessResponse {
  return (
    response &&
    response.message === "tokens_issued" &&
    response.data &&
    "access_token" in response.data &&
    "id_token" in response.data
  );
}

export function isSSOErrorResponse(
  response: any
): response is SSOErrorResponse {
  return (
    response &&
    typeof response.message === "string" &&
    response.message !== "tokens_issued"
  );
}

export function isSSOTokenExchangeFailedResponse(
  response: any
): response is SSOTokenExchangeFailedResponse {
  return (
    response &&
    response.message === "token_exchange_failed" &&
    response.data &&
    "request_id" in response.data
  );
}

/**
 * Error Messages
 */

export const SSO_ERROR_MESSAGES = {
  // 400 errors
  invalid_payload: "Invalid authentication request. Please try again.",
  invalid_or_expired_auth_code:
    "Your authentication link has expired. Please request a new one.",

  // 401 errors
  token_issue_failed:
    "Authentication failed. Please contact support if this issue persists.",

  // 404 errors
  patient_not_found:
    "No patient account found. Please contact your dental office to set up your account.",

  // 500 errors
  sso_password_decrypt_failed: "Oops, something went wrong",
  token_exchange_failed: "Oops, something went wrong",

  // Network/unknown errors
  network_error:
    "Unable to connect. Please check your internet connection and try again.",
  unknown_error: "Oops, something went wrong",
} as const;

/**
 * Storage Keys
 */

export const SSO_STORAGE_KEYS = {
  TOKENS: "sso_tokens",
  AUTH_STATE: "sso_auth_state",
  LAST_LOGIN: "sso_last_login",
  POST_LOGIN_REDIRECT: "sso_post_login_redirect",
} as const;
